import { aiErrorText } from "@/lib/api";
import { useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ICONS, COLORS, IDEAS, FUN } from "@/components/appbuilder/missions";

const chip = (active) => `rounded-2xl border-4 px-3 py-2 font-bold text-sm shadow-sm transition-transform hover:-translate-y-0.5 ${active ? "border-blue-600 bg-sky-100" : "border-white bg-white"}`;
const input = "w-full bg-white border-4 border-sky-100 rounded-2xl px-4 py-2.5 font-semibold focus:outline-none focus:border-sky-300";

export const IdeaMission = ({ project, update, lang, tx }) => {
  const preset = IDEAS.find((i) => i[lang] === project.idea || i.es === project.idea || i.en === project.idea);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {IDEAS.map((i) => {
          const Icon = ICONS[i.icon];
          return (
            <button key={i.id} type="button" onClick={() => update({ idea: i[lang] })} className={`${chip(preset?.id === i.id)} flex items-center gap-2 text-left text-blue-950`} data-testid={`idea-${i.id}`}>
              <Icon className="w-5 h-5 shrink-0" /> {i[lang]}
            </button>
          );
        })}
      </div>
      <input value={preset ? "" : project.idea} onChange={(e) => update({ idea: e.target.value.slice(0, 80) })} placeholder={tx.other} className={input} data-testid="idea-custom-input" />
    </div>
  );
};

export const NameMission = ({ project, update, lang, tx, api }) => {
  const [names, setNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const ask = async () => {
    setLoading(true);
    try {
      const r = await api.post("/business/names", { product: `${lang === "es" ? "una app: " : "an app: "}${project.idea}`, language: lang });
      setNames(r.data.names);
    } catch (e) {
      toast.error(aiErrorText(e, lang, "Lumi :("));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="space-y-3">
      <input value={project.name} onChange={(e) => update({ name: e.target.value.slice(0, 40) })} placeholder={tx.namePh} className={`${input} font-fredoka text-xl`} data-testid="app-name-input" />
      <button type="button" onClick={ask} disabled={loading || !project.idea} className="bg-yellow-300 rounded-full px-4 py-2 font-bold text-blue-950 inline-flex items-center gap-2 shadow disabled:opacity-60" data-testid="app-name-ask-lumi">
        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {tx.askLumi}
      </button>
      <div className="flex flex-wrap gap-2">
        {names.map((n, i) => (
          <button key={i} type="button" onClick={() => update({ name: n.name.slice(0, 40) })} className="bg-pink-100 hover:bg-pink-200 rounded-full px-3 py-1.5 font-bold text-sm text-blue-950" data-testid={`app-name-suggestion-${i}`}>{n.name}</button>
        ))}
      </div>
    </div>
  );
};

export const LogoMission = ({ project, update, tx }) => {
  const logo = project.logo || { icon: "Lightbulb", color: "yellow", shape: "rounded" };
  const set = (k, v) => update({ logo: { ...logo, [k]: v } });
  return (
    <div className="space-y-4">
      <p className="font-bold text-blue-950">{tx.icon}</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(ICONS).map(([k, Icon]) => (
          <button key={k} type="button" onClick={() => set("icon", k)} className={chip(project.logo?.icon === k)} data-testid={`logo-icon-${k}`} aria-label={k}><Icon className="w-6 h-6 text-blue-950" /></button>
        ))}
      </div>
      <p className="font-bold text-blue-950">{tx.color}</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(COLORS).map(([k, c]) => (
          <button key={k} type="button" onClick={() => set("color", k)} className={`${c.bg} w-10 h-10 rounded-full border-4 ${logo.color === k ? "border-blue-600" : "border-white"} shadow`} data-testid={`logo-color-${k}`} aria-label={k} />
        ))}
      </div>
      <p className="font-bold text-blue-950">{tx.shape}</p>
      <div className="flex gap-2">
        <button type="button" onClick={() => set("shape", "round")} className={chip(logo.shape === "round")} data-testid="logo-shape-round">{tx.round}</button>
        <button type="button" onClick={() => set("shape", "rounded")} className={chip(logo.shape === "rounded")} data-testid="logo-shape-rounded">{tx.square}</button>
      </div>
    </div>
  );
};

export const FunMission = ({ project, update, lang }) => (
  <div className="grid grid-cols-2 gap-3">
    {FUN.map((f) => {
      const Icon = f.icon;
      return (
        <button key={f.id} type="button" onClick={() => update({ fun: f.id })} className={`${chip(project.fun === f.id)} flex flex-col items-center gap-2 py-4 text-blue-950`} data-testid={`fun-${f.id}`}>
          <Icon className="w-8 h-8" /> {f[lang]}
        </button>
      );
    })}
  </div>
);

export { input as inputClass };
